import { jsx } from "panel";
import { selx } from "clsx";
import { SegmentFilterEl } from "common/charts/misc/segment-filter";
import { InsightsPieChartEl } from "common/report/insights/charts/insights-pie-chart/panel";
import { InsightsMetricChartEl } from "common/report/insights/charts/insights-metric-chart/panel";
import { InsightsLineChartEl } from "common/report/insights/charts/insights-line-chart/panel";
import { InsightsBarChartEl } from "common/report/insights/charts/insights-bar-chart/panel";
import { InsightsTableChartEl } from "common/report/insights/charts/insights-table-chart/panel";
import { FunnelsStepChartEl } from "common/report/insights/charts/funnels-step-chart/panel";
import { InsightsTopPathsChartEl } from "common/report/insights/charts/insights-top-path-chart";
import { ChartType } from "common/types/reports/bookmark";
import { RetentionCurveChartEl } from "common/report/insights/charts/retention-curve-chart/panel";
import styles from "./index.module.styl";
export function template() {
  const {
    report
  } = this.state;
  const bookmarkParams = report.toBookmarkData();
  const chartRenderFn = getChartRenderFn(report.displayOptions.chartType);
  return /* @__PURE__ */ jsx("div", { sel: selx(styles.chart, "is-".concat(report.displayOptions.chartType)), "data-sentry-component": "template", "data-sentry-source-file": "template.tsx" }, chartRenderFn(this, bookmarkParams));
}
function getChartRenderFn(chartType) {
  switch (chartType) {
    case ChartType.Bar:
      return getBarChart;
    case ChartType.Column:
    case ChartType.StackedColumn:
    case ChartType.Line:
      return getLineChart;
    case ChartType.Pie:
      return getPieChart;
    case ChartType.Table:
      return getTableChart;
    case ChartType.Metric:
      return getMetricChart;
    case ChartType.FunnelSteps:
      return getStepChart;
    case ChartType.FunnelTopPaths:
      return getTopPaths;
    case ChartType.RetentionCurve:
      return getRetentionCurveChart;
    default:
      return null;
  }
}
function getLineChart(chartDisplay, bookmarkParams) {
  return /* @__PURE__ */ jsx(InsightsLineChartEl, { elref: "insightsLineChart", paramsAndResults: {
    params: bookmarkParams,
    results: chartDisplay.queryResults
  }, loaded: true, showAnnotations: !chartDisplay.helpers.isDownloadMode(), popoverCallback: chartDisplay.helpers.lineChartPopoverCallback, onPopoverAction: chartDisplay.helpers.handleLineChartPopoverAction, "data-sentry-element": "InsightsLineChartEl", "data-sentry-component": "getLineChart", "data-sentry-source-file": "template.tsx" });
}
function getPieChart(chartDisplay, bookmarkParams) {
  return /* @__PURE__ */ jsx(InsightsPieChartEl, { sel: ".pie-chart", loaded: true, paramsAndResults: {
    params: bookmarkParams,
    results: chartDisplay.queryResults
  }, popoverCallback: chartDisplay.segmentPopoverCallback, onPopoverAction: (detail) => chartDisplay.handleSegmentPopoverAction(detail), "data-sentry-element": "InsightsPieChartEl", "data-sentry-component": "getPieChart", "data-sentry-source-file": "template.tsx" });
}
function getTableChart(chartDisplay, bookmarkParams) {
  return /* @__PURE__ */ jsx(InsightsTableChartEl, { bookmarkParams, results: chartDisplay.queryResults, resizingLayout: true, canUseViewN: chartDisplay.attr("can-use-view-n"), downloadMode: chartDisplay.helpers.isDownloadMode(), popoverCallback: chartDisplay.segmentPopoverCallback, popoverActionCallback: chartDisplay.handleSegmentPopoverAction, sortChangeCallback: chartDisplay.helpers.dataTableSortChange, columnWidthOverrides: chartDisplay.tableChartColumnWidthOverrides, columnResizeCallback: chartDisplay.helpers.dataTableColumnResize, "data-sentry-element": "InsightsTableChartEl", "data-sentry-component": "getTableChart", "data-sentry-source-file": "template.tsx" });
}
function getMetricChart(chartDisplay, bookmarkParams) {
  return /* @__PURE__ */ jsx(InsightsMetricChartEl, { paramsAndResults: {
    params: bookmarkParams,
    results: chartDisplay.queryResults
  }, popoverCallback: chartDisplay.helpers.metricChartPopoverCallback, onPopoverAction: (data) => chartDisplay.handleSegmentPopoverAction(data), "data-sentry-element": "InsightsMetricChartEl", "data-sentry-component": "getMetricChart", "data-sentry-source-file": "template.tsx" });
}
function getBarChart(chartDisplay, bookmarkParams) {
  return /* @__PURE__ */ jsx(null, null, /* @__PURE__ */ jsx(InsightsBarChartEl, { barPopoverCallback: chartDisplay.segmentPopoverCallback, downloadMode: chartDisplay.helpers.isDownloadMode(), elref: "barChart", enableSort: true, onBarPopoverAction: (options) => chartDisplay.handleSegmentPopoverAction(options), onColumnWidthChange: (columnWidths) => chartDisplay.helpers.updateBarColumnWidths(columnWidths), onSortingChange: (sortConfig) => chartDisplay.helpers.updateBarSortConfig(sortConfig), paramsAndResults: {
    params: bookmarkParams,
    results: chartDisplay.queryResults
  }, "data-sentry-element": "InsightsBarChartEl", "data-sentry-source-file": "template.tsx" }), !chartDisplay.helpers.isDownloadMode() && /* @__PURE__ */ jsx(SegmentFilterEl, { sel: selx(styles.segmentFilter), on: {
    "export-csv": chartDisplay.helpers.handleExportCsv
  } }));
}
function getStepChart(chartDisplay, bookmarkParams) {
  return /* @__PURE__ */ jsx(FunnelsStepChartEl, { ...{
    bookmarkData: {
      queryResults: chartDisplay.queryResults,
      bookmark: bookmarkParams
    },
    enableSegmentClick: true,
    loaded: true,
    // min value chosen to match prior width (when step widths were linked to table columns)
    minStepWidth: 266,
    maxStepWidth: 600,
    onChangeControlGroup: chartDisplay.helpers.handleChangeControlGroup,
    popoverAction: chartDisplay.helpers.stepsChartPopoverAction,
    resizingLayout: true
  }, "data-sentry-element": "FunnelsStepChartEl", "data-sentry-component": "getStepChart", "data-sentry-source-file": "template.tsx" });
}
function getTopPaths(chartDisplay, bookmarkParams) {
  return /* @__PURE__ */ jsx(InsightsTopPathsChartEl, { params: {
    bookmark: bookmarkParams,
    insightsResponse: chartDisplay.queryResults,
    disableClickNode: true
  }, "data-sentry-element": "InsightsTopPathsChartEl", "data-sentry-component": "getTopPaths", "data-sentry-source-file": "template.tsx" });
}
function getRetentionCurveChart(chartDisplay, bookmarkParams) {
  return /* @__PURE__ */ jsx(RetentionCurveChartEl, { ...{
    bookmarkData: {
      bookmark: bookmarkParams,
      queryResults: chartDisplay.queryResults
    },
    className: "retention-curve-chart",
    showPopover: true
  }, "data-sentry-element": "RetentionCurveChartEl", "data-sentry-component": "getRetentionCurveChart", "data-sentry-source-file": "template.tsx" });
}
